import {
  MenuItem,
  MenuSeparator,
  Tooltip,
  useConfirmModal,
} from '@affine/component';
import { Guard } from '@affine/core/components/guard';
import { useBlockSuiteMetaHelper } from '@affine/core/components/hooks/affine/use-block-suite-meta-helper';
import { useTrashDoc } from '@affine/core/components/hooks/affine/use-trash-doc';
import { useSplitViewViewportAvailable } from '@affine/core/components/hooks/use-split-view-viewport-available';
import { IsFavoriteIcon } from '@affine/core/components/pure/icons';
import { DocsService } from '@affine/core/modules/doc';
import { CompatibleFavoriteItemsAdapter } from '@affine/core/modules/favorite';
import { DocShareMenuItem } from '@affine/core/modules/share-menu/view/doc-share-menu-item';
import { DocDeleteAppUsage } from '@affine/core/modules/streamlain/native-app/doc-delete-app-usage';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import {
  DeleteIcon,
  DuplicateIcon,
  InformationIcon,
  OpenInNewIcon,
  SplitViewIcon,
} from '@blocksuite/icons/rc';
import { useFramework, useLiveData, useServices } from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

import { defineNavigationPanelNodeOperations } from '../../tree/operation-registry';
import type { NodeOperation } from '../../tree/types';

const DOC_NODE_OPERATION_IDS = {
  info: 'doc:info',
  share: 'doc:share',
  duplicate: 'doc:duplicate',
  openNewTab: 'doc:open-new-tab',
  openSplitView: 'doc:open-split-view',
  favorite: 'doc:favorite',
  trashSeparator: 'doc:trash-separator',
  trash: 'doc:trash',
} as const;

type DocNodeOperationId =
  (typeof DOC_NODE_OPERATION_IDS)[keyof typeof DOC_NODE_OPERATION_IDS];

const OpenInSplitViewMenuItem = ({ onClick }: { onClick: () => void }) => {
  const t = useI18n();
  const viewportAvailable = useSplitViewViewportAvailable();

  return (
    <Tooltip
      content={
        viewportAvailable
          ? undefined
          : t['com.streamlain.workbench.split-view.viewport-too-small']()
      }
      side="right"
    >
      <div>
        <MenuItem
          prefixIcon={<SplitViewIcon />}
          onClick={onClick}
          disabled={!viewportAvailable}
        >
          {t['com.streamlain.workbench.split-view.page-menu-open']()}
        </MenuItem>
      </div>
    </Tooltip>
  );
};

export const useNavigationPanelDocNodeOperations = (
  docId: string,
  options: {
    openInfoModal: () => void;
    openNodeCollapsed: () => void;
    hideTrashOperation?: boolean;
    trashOperationUnavailableReason?: string;
  }
): NodeOperation[] => {
  const t = useI18n();
  const { workbenchService, docsService, compatibleFavoriteItemsAdapter } =
    useServices({
      DocsService,
      WorkbenchService,
      CompatibleFavoriteItemsAdapter,
    });
  const { openConfirmModal } = useConfirmModal();
  const framework = useFramework();
  const trashDoc = useTrashDoc();

  const docRecord = useLiveData(docsService.list.doc$(docId));

  const favorite = useLiveData(
    useMemo(() => {
      return compatibleFavoriteItemsAdapter.isFavorite$(docId, 'doc');
    }, [docId, compatibleFavoriteItemsAdapter])
  );

  const { duplicate } = useBlockSuiteMetaHelper();
  const handleDuplicate = useCallback(() => {
    duplicate(docId, true);
    track.$.navigationPanel.docs.createDoc();
  }, [docId, duplicate]);
  const handleOpenInfoModal = useCallback(() => {
    track.$.docInfoPanel.$.open();
    options.openInfoModal();
  }, [options]);

  const handleMoveToTrash = useCallback(() => {
    if (!docRecord) {
      return;
    }
    openConfirmModal({
      title: t['com.streamlain.moveToTrash.title'](),
      description: t['com.streamlain.moveToTrash.confirmModal.description']({
        title: docRecord.title$.value,
      }),
      children: (
        <DocDeleteAppUsage
          framework={framework}
          target={{ kind: 'doc', ids: [docId] }}
          operation="trash"
        />
      ),
      confirmText: t['com.streamlain.moveToTrash.confirmModal.confirm'](),
      cancelText: t['com.streamlain.moveToTrash.confirmModal.cancel'](),
      confirmButtonOptions: {
        variant: 'error',
      },
      async onConfirm() {
        await trashDoc(docRecord);
        track.$.navigationPanel.docs.deleteDoc({
          control: 'button',
        });
      },
    });
  }, [docId, docRecord, framework, openConfirmModal, t, trashDoc]);

  const handleOpenInNewTab = useCallback(() => {
    workbenchService.workbench.openDoc(docId, {
      at: 'new-tab',
    });
    track.$.navigationPanel.docs.openDoc();
    track.$.navigationPanel.organize.openInNewTab({
      type: 'doc',
    });
  }, [docId, workbenchService]);

  const handleOpenInSplitView = useCallback(() => {
    workbenchService.workbench.openDoc(docId, {
      at: 'beside',
    });
    track.$.navigationPanel.docs.openDoc();
    track.$.navigationPanel.organize.openInSplitView({
      type: 'doc',
    });
  }, [docId, workbenchService.workbench]);

  const handleToggleFavoriteDoc = useCallback(() => {
    compatibleFavoriteItemsAdapter.toggle(docId, 'doc');
    track.$.navigationPanel.organize.toggleFavorite({
      type: 'doc',
    });
  }, [docId, compatibleFavoriteItemsAdapter]);

  return useMemo(
    () =>
      defineNavigationPanelNodeOperations<DocNodeOperationId>([
        // sl-jmyl.8: "Verknüpfte Seite hinzufügen" inline button removed
        {
          id: DOC_NODE_OPERATION_IDS.info,
          index: 50,
          view: (
            <MenuItem
              prefixIcon={<InformationIcon />}
              onClick={handleOpenInfoModal}
            >
              {t['com.streamlain.page-properties.page-info.view']()}
            </MenuItem>
          ),
        },
        // sl-jmyl.8: "Verknüpfte Seite hinzufügen" menu item removed
        {
          id: DOC_NODE_OPERATION_IDS.share,
          index: 9998,
          view: <DocShareMenuItem docId={docId} />,
        },
        {
          id: DOC_NODE_OPERATION_IDS.duplicate,
          index: 99,
          view: (
            <MenuItem prefixIcon={<DuplicateIcon />} onClick={handleDuplicate}>
              {t['com.streamlain.header.option.duplicate']()}
            </MenuItem>
          ),
        },
        {
          id: DOC_NODE_OPERATION_IDS.openNewTab,
          index: 99,
          view: (
            <MenuItem
              prefixIcon={<OpenInNewIcon />}
              onClick={handleOpenInNewTab}
            >
              {t['com.streamlain.workbench.tab.page-menu-open']()}
            </MenuItem>
          ),
        },
        {
          id: DOC_NODE_OPERATION_IDS.openSplitView,
          index: 100,
          view: <OpenInSplitViewMenuItem onClick={handleOpenInSplitView} />,
        },
        {
          id: DOC_NODE_OPERATION_IDS.favorite,
          index: 199,
          view: (
            <MenuItem
              prefixIcon={<IsFavoriteIcon favorite={favorite} />}
              onClick={handleToggleFavoriteDoc}
            >
              {favorite
                ? t['com.streamlain.favoritePageOperation.remove']()
                : t['com.streamlain.favoritePageOperation.add']()}
            </MenuItem>
          ),
        },
        ...(options.hideTrashOperation === true
          ? options.trashOperationUnavailableReason
            ? [
                {
                  id: DOC_NODE_OPERATION_IDS.trashSeparator,
                  index: 9999,
                  view: <MenuSeparator key="menu-separator" />,
                },
                {
                  id: DOC_NODE_OPERATION_IDS.trash,
                  index: 10000,
                  view: (
                    <MenuItem prefixIcon={<InformationIcon />} disabled>
                      {options.trashOperationUnavailableReason}
                    </MenuItem>
                  ),
                },
              ]
            : []
          : [
              {
                id: DOC_NODE_OPERATION_IDS.trashSeparator,
                index: 9999,
                view: <MenuSeparator key="menu-separator" />,
              },
              {
                id: DOC_NODE_OPERATION_IDS.trash,
                index: 10000,
                view: (
                  <Guard docId={docId} permission="Doc_Trash">
                    {canMoveToTrash => (
                      <MenuItem
                        type={'danger'}
                        prefixIcon={<DeleteIcon />}
                        onClick={handleMoveToTrash}
                        disabled={!canMoveToTrash}
                      >
                        {t['com.streamlain.moveToTrash.title']()}
                      </MenuItem>
                    )}
                  </Guard>
                ),
              },
            ]),
      ]),
    [
      docId,
      favorite,
      handleDuplicate,
      handleMoveToTrash,
      handleOpenInNewTab,
      handleOpenInSplitView,
      handleOpenInfoModal,
      handleToggleFavoriteDoc,
      options.hideTrashOperation,
      options.trashOperationUnavailableReason,
      t,
    ]
  );
};
